import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { useIntl } from "../../../../shared/core/i18n/use-intl";
import { circleSuccessIcon } from "../../../assets/images";
import { theme } from "../../styles/theme";
import { ErrorCircle } from "../error-circle";
import { ErrorMessage } from "../error-message";
import { Spinner } from "../spinner";
import { ExpirationIcon } from "../svg/expiration-icon";
import { usePollingStateSession } from "./usePollingSessionState";

export enum STATUS {
  STARTED = "STARTED",
  PENDING = "PENDING",
  DONE = "DONE",
  REFUSED = "REFUSED",
  CANCELLED = "CANCELLED",
  FAILED = "FAILED",
}

interface PollingSessionStateProps {
  sessionToken: string | undefined;
  dismissPollingSessionStateModal: () => void;
}

export const PollingSessionState = ({ sessionToken, dismissPollingSessionStateModal }: PollingSessionStateProps) => {
  const { sessionPolling, isDone, isExpired, isPending, isStarted, error } = usePollingStateSession(
    sessionToken,
    dismissPollingSessionStateModal,
  );

  const status = sessionPolling?.status;
  const { formatMessage } = useIntl();
  const [message, setMessage] = useState<string | React.JSX.Element>("");
  const [isRefused, setIsRefused] = useState<boolean>(false);

  useEffect(() => {
    if (isExpired) {
      setMessage(formatMessage("addRecipient.pollingModalSession.messageExpired"));
    } else {
      if (status === STATUS.CANCELLED) {
        setMessage(formatMessage("addRecipient.pollingModalSession.messageCancelled"));
      }
      if (status === STATUS.FAILED) {
        setMessage(formatMessage("addRecipient.pollingModalSession.messageFailed"));
      }
      if (status === STATUS.REFUSED) {
        setIsRefused(true);
        setMessage(formatMessage("addRecipient.pollingModalSession.messageRefused"));
      }
      if (status === STATUS.DONE) {
        setMessage(formatMessage("addRecipient.pollingModalSession.messageDone"));
      }
      if (isPending) {
        const pendingMessage = (
          <div style={{ textAlign: "center" }}>
            {formatMessage("addRecipient.pollingModalSession.messagePending")}
            <br />
            {formatMessage("addRecipient.pollingModalSession.messagePendingCheck")}
          </div>
        );
        setMessage(pendingMessage);
      }
      if (status === STATUS.STARTED) {
        setMessage(formatMessage("addRecipient.pollingModalSession.messageStarted"));
      }
    }
  }, [status, isExpired]);

  const getContentSpinnerByStatus = () => {
    let content;

    if (isExpired) {
      content = (
        <DivSpinner>
          <ExpirationIcon height={50} width={50} />
        </DivSpinner>
      );
    } else if (isDone) {
      content = (
        <DivSpinner>
          <SuccessLogoImage src={circleSuccessIcon} alt="" />
        </DivSpinner>
      );
    } else if (isStarted || isPending) {
      content = (
        <DivSpinner>
          <Spinner color="#dddddd" size="50px" />
        </DivSpinner>
      );
    } else {
      content = (
        <DivSpinner>
          <ErrorCircle size={50} />
        </DivSpinner>
      );
    }

    return content;
  };

  return (
    <Container>
      <Header>
        <Title>{formatMessage("addRecipient.pollingModalSession.title")}</Title>
      </Header>
      {getContentSpinnerByStatus()}
      <Description isRefused={isRefused}>{message}</Description>
      {sessionPolling?.error && <StyledErrorMessage>{sessionPolling.error}</StyledErrorMessage>}
      {error && <StyledErrorMessage>{formatMessage("addRecipient.pollingModalSession.error")}</StyledErrorMessage>}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 20px;
`;
const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
`;
const Title = styled.h2`
  ${theme.boldText};
  font-size: 1.375rem;
  margin: 0;
  flex-shrink: 1;
  flex-grow: 1;
`;
const Description = styled.span<{ isRefused: boolean }>`
  ${theme.text};
  ${({ isRefused }) => `color: ${isRefused ? "#ff4242" : "#000000"}`};
  font-size: 0.9375rem;
  margin: 10px 0;
  align-self: center;
`;
const StyledErrorMessage = styled(ErrorMessage)`
  margin-top: 10px;
`;
const DivSpinner = styled.div`
  align-self: center;
`;
const SuccessLogoImage = styled.img`
  align-self: center;
  width: 200px;
  height: 200px;
`;
